"use client";

import { c as _c } from "react/compiler-runtime";
import DemographicEditDialog from "../../dialogs/demographic-edit-dialog";
import DemographicEditHistoryDialog from "../../dialogs/demographic-edit-history-dialog";
import { FiClock, FiEdit } from "react-icons/fi";
import LabeledValue from "@/components/labeled-value";
import ClickableEmail from "@/components/patient/clickable-email";
import HiddenContact from "@/components/patient/hidden-contact";
import { Button } from "@/components/ui";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { useLocations } from "@/hooks/use-locations";
import { HIDDEN_CONTACT, calculateAge, calculateRelativeDuration, formatAddress, formatDate, formatName, formatPhoneNumberOrHidden, formatToHumanReadable } from "@/lib/format-data";
import { clickableText, cx } from "@/lib/tailwind-utils";
import { PatientDetail } from "@/types/patient";
interface DemographicsSectionProps {
  patient: PatientDetail;
  shouldUseTwoColumns: boolean;
}
export default function DemographicsSection(t0) {
  const $ = _c(83);
  const {
    patient,
    shouldUseTwoColumns
  } = t0;
  const {
    locations
  } = useLocations();
  let t1;
  if ($[0] !== locations) {
    t1 = locationId => {
      if (!locationId) {
        return;
      }
      const location = locations.find(loc => loc.id === locationId);
      return location?.name;
    };
    $[0] = locations;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getLocationName = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Button variant="secondary" size="sm" icon={FiEdit}>Edit</Button>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== patient.id) {
    t3 = <DemographicEditDialog patientId={patient.id}>{t2}</DemographicEditDialog>;
    $[3] = patient.id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = shouldUseTwoColumns ? "grid-cols-2" : "grid-cols-1";
  let t5;
  if ($[5] !== t4) {
    t5 = cx("grid gap-x-6", t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== patient.firstName || $[8] !== patient.lastName) {
    t6 = formatName(patient.firstName, patient.lastName);
    $[7] = patient.firstName;
    $[8] = patient.lastName;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <LabeledValue label="Name" value={<span className="whitespace-nowrap">{t6}</span>} />;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== patient.preferredName) {
    t8 = patient.preferredName && <LabeledValue label="Preferred Name" value={patient.preferredName} />;
    $[12] = patient.preferredName;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== patient.dateOfBirth) {
    t9 = patient.dateOfBirth ? <div className="leading-tight"><div>{formatDate(patient.dateOfBirth)}</div><div className="text-muted-foreground">({calculateAge(patient.dateOfBirth)} yrs)</div></div> : undefined;
    $[14] = patient.dateOfBirth;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <LabeledValue label="Date of Birth" value={t9} />;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== patient.gender) {
    t11 = patient.gender ? formatToHumanReadable(patient.gender) : undefined;
    $[18] = patient.gender;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <LabeledValue label="Gender" value={t11} />;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== patient.pronouns) {
    t13 = patient.pronouns && <LabeledValue label="Pronouns" value={patient.pronouns} />;
    $[22] = patient.pronouns;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== patient.createdDate) {
    t14 = patient.createdDate ? <div className="leading-tight"><div>{formatDate(patient.createdDate)}</div><div className="text-muted-foreground">({calculateRelativeDuration(patient.createdDate)})</div></div> : undefined;
    $[24] = patient.createdDate;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14) {
    t15 = <LabeledValue label="Patient Since" value={t14} />;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== patient) {
    t16 = formatPhoneNumberOrHidden(patient);
    $[28] = patient;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16) {
    t17 = <LabeledValue label="Phone" value={<span className="whitespace-nowrap">{t16}</span>} />;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== patient.email || $[33] !== patient.hasEmail || $[34] !== patient.id) {
    t18 = patient.email ? <ClickableEmail email={patient.email} patientId={patient.id} className="break-all lowercase" /> : patient.hasEmail ? <HiddenContact className="break-all" /> : undefined;
    $[32] = patient.email;
    $[33] = patient.hasEmail;
    $[34] = patient.id;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t18) {
    t19 = <LabeledValue label="Email" value={t18} />;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== patient.externalId) {
    t20 = <LabeledValue label="External ID" value={patient.externalId} />;
    $[38] = patient.externalId;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== patient.leadSource) {
    t21 = patient.leadSource && <LabeledValue label="Source" value={formatToHumanReadable(patient.leadSource)} />;
    $[40] = patient.leadSource;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== getLocationName || $[43] !== patient.primaryLocationId) {
    t22 = getLocationName(patient.primaryLocationId);
    $[42] = getLocationName;
    $[43] = patient.primaryLocationId;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t22) {
    t23 = <LabeledValue label="Primary Location" value={t22} />;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const t24 = shouldUseTwoColumns && "col-span-2";
  let t25;
  if ($[47] !== t24) {
    t25 = cx(t24);
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== patient.address || $[50] !== patient.addressLineTwo || $[51] !== patient.city || $[52] !== patient.hasEmail || $[53] !== patient.hasPhoneNumber || $[54] !== patient.state || $[55] !== patient.zipCode) {
    t26 = patient.address ? formatAddress(patient.address, patient.city, patient.state, patient.zipCode, patient.addressLineTwo) : patient.hasPhoneNumber || patient.hasEmail ? HIDDEN_CONTACT : undefined;
    $[49] = patient.address;
    $[50] = patient.addressLineTwo;
    $[51] = patient.city;
    $[52] = patient.hasEmail;
    $[53] = patient.hasPhoneNumber;
    $[54] = patient.state;
    $[55] = patient.zipCode;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t26) {
    t27 = <LabeledValue label="Address" value={t26} />;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t25 || $[60] !== t27) {
    t28 = <div className={t25}>{t27}</div>;
    $[59] = t25;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t10 || $[63] !== t12 || $[64] !== t13 || $[65] !== t15 || $[66] !== t17 || $[67] !== t19 || $[68] !== t20 || $[69] !== t21 || $[70] !== t23 || $[71] !== t28 || $[72] !== t5 || $[73] !== t7 || $[74] !== t8) {
    t29 = <div className={t5}>{t7}{t8}{t10}{t12}{t13}{t15}{t17}{t19}{t20}{t21}{t23}{t28}</div>;
    $[62] = t10;
    $[63] = t12;
    $[64] = t13;
    $[65] = t15;
    $[66] = t17;
    $[67] = t19;
    $[68] = t20;
    $[69] = t21;
    $[70] = t23;
    $[71] = t28;
    $[72] = t5;
    $[73] = t7;
    $[74] = t8;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Button variant="ghost" size="sm" icon={FiClock} className={cx("!p-0 hover:!bg-transparent", clickableText)}>Show history</Button>;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== patient.id) {
    t31 = <DemographicEditHistoryDialog patientId={patient.id}>{t30}</DemographicEditHistoryDialog>;
    $[77] = patient.id;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== t29 || $[80] !== t3 || $[81] !== t31) {
    t32 = <CollapsibleSection title="Demographics" persistKey="demographics" actions={t3}>{t29}{t31}</CollapsibleSection>;
    $[79] = t29;
    $[80] = t3;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  return t32;
}